import React from "react";
import { graphql } from "gatsby";
import Layout from "../components/Layout";
import BlogDetailHeader from "../components/BlogDetailHeader";
import { PostCallToAction } from "../components/CalltoAction";
import replaceTechPlusMore from "../helpers/replaceTechPlusMore";
import { SideBar } from "../components/Sidebar";
import { ContentIndex } from "../components/ContentIndex";
import Wysiwyg from "../components/Wysiwyg";
import RelatedContent from "../components/Related/RelatedContent";
import { Author } from "../components/AuthorNames";
import { YoastSeo } from "../components/SEO";

const PostDetail = ({
  data: {
    wpPost: {
      title,
      content,
      index,
      seo,
      date,
      author,
      authors,
      featuredImage,
      post: { contenttype, relatedPosts },
      cta: { ctaPerson, ctaTitle, ctaTitleSmall },
    },
    enriseSettings: {
      postCtaPerson,
      postCtaTitleSmall,
      postCtaStickyDescription,
      postCtaContact,
      postCtaContactTitle,
    },
  },
}) => {
  const normalizedAuthors = (authors?.nodes ?? [author?.node]) as Author[];
  return (
    <Layout dark>
      <YoastSeo
        title={title}
        yoast={seo.fullHead}
        overrideOGType={contenttype}
        author={normalizedAuthors.map((a) => a.name).join(", ")}
      />
      <BlogDetailHeader
        title={title}
        date={date}
        authors={normalizedAuthors}
        backgroundImage={featuredImage?.node}
      />
      <main className="relative z-20 w-full">
        <div className="container grid grid-cols-3 gap-x-12 lg:grid-cols-detail">
          <ContentIndex index={index} />
          <section className="col-span-full py-12 lg:col-span-2 xl:col-span-1 xl:col-start-2">
            <Wysiwyg>{content}</Wysiwyg>
          </section>
          <SideBar
            title={ctaTitleSmall || postCtaTitleSmall}
            image={postCtaPerson?.employee?.image}
            contactTitle={postCtaContactTitle}
            contact={postCtaContact?.uri}
            description={replaceTechPlusMore(
              postCtaStickyDescription,
              "w-10 -ml-1 align-bottom text-brand"
            )}
          />
        </div>
        <PostCallToAction
          person={ctaPerson}
          title={ctaTitle}
          titleSmall={ctaTitleSmall}
        />
        <RelatedContent related={relatedPosts} />
      </main>
    </Layout>
  );
};

export default PostDetail;

export const query = graphql`
  query ($id: String!) {
    wpPost(id: { eq: $id }) {
      title
      content
      index {
        id
        title
      }
      date(formatString: "D MMMM Y", locale: "nl")
      uri
      modified
      seo {
        fullHead
      }
      authors {
        nodes {
          name
          employee {
            node {
              uri
            }
          }
          avatarImage {
            node {
              altText
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 151)
                }
              }
            }
          }
        }
      }
      author {
        node {
          name
          employee {
            node {
              uri
            }
          }
          avatarImage {
            node {
              altText
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 151)
                }
              }
            }
          }
        }
      }
      post {
        contenttype
        relatedPosts {
          __typename
          ... on WpPost {
            ...PostTile
          }
          ... on WpCase {
            ...CaseTile
          }
        }
      }
      featuredImage {
        node {
          altText
          localFile {
            childImageSharp {
              gatsbyImageData(height: 700)
            }
          }
        }
      }
      cta {
        ctaPerson {
          ... on WpEmployee {
            title
            employee {
              phone
              email
              image {
                altText
                localFile {
                  childImageSharp {
                    gatsbyImageData(layout: FIXED, width: 240)
                  }
                }
              }
            }
          }
        }
        ctaTitle
        ctaTitleSmall
      }
    }
    enriseSettings {
      postCtaTitleSmall
      postCtaStickyDescription
      postCtaContactTitle
      postCtaContact {
        ... on WpUniformResourceIdentifiable {
          uri
        }
      }
      postCtaPerson {
        ... on WpEmployee {
          title
          employee {
            phone
            email
            image {
              altText
              localFile {
                childImageSharp {
                  gatsbyImageData(layout: FIXED, width: 240)
                }
              }
            }
          }
        }
      }
    }
  }
`;
